import { Div, Button, Input } from '@codesuma/baseline'
import { DatePicker } from '../date-picker'
import { IDateParts, convert } from '../../services/convert'
import calendar, { IDayCell } from '../../services/calendar'
import settings from '../../services/settings'
import { dayLine } from '../../helpers/day-line'
import { paleYear } from '../../helpers/year'
import style from './index.module.css'

// The shapes a wizard step takes: typing a name, picking one of a short list,
// or naming a day. Each emits its value on change and leaves the flow to the
// shell.
//
// Shared by the task, occasion and event pages, which is why the wording is
// passed in rather than written here — the three ask for different things.

// The naming step. Emits 'text' with the current value, and 'enter' when the
// keyboard's return key is pressed, which is the same as tapping ادامه.
export const TitleSlide = (
    value: string,
    { heading: title, placeholder }: { heading: string, placeholder: string },
) => {
    const base = Div()
    base.addClass(style.body)

    const heading = Div(title)
    heading.addClass(style.stepTitle)

    const input = Input(placeholder)
    input.addClass(style.field)
    input.attr('dir', 'auto')
    input.setValue(value)
    // Not 'change': the input also fires a native change on blur, which would
    // reach this listener as a raw Event instead of the payload.
    input.on('input', ({ value: next }: { value: string }) => base.emit('text', next))
    input.on('enter', () => base.emit('enter'))

    base.append(heading, input)

    return Object.assign(base, { focus: () => input.focus() })
}

export interface IChoice<T> {
    value: T
    label: string
    // The line under the label, where a choice needs a word of explanation.
    note?: string
}

// Steps 2, 3 and 4 are all "pick one of these". Emits 'pick' with the chosen
// value.
export const ChoiceSlide = <T extends string>(title: string, choices: IChoice<T>[], selected: T) => {
    const base = Div()
    base.addClass(style.body)

    const heading = Div(title)
    heading.addClass(style.stepTitle)
    base.append(heading)

    const group = Div()
    group.addClass(style.choices)
    group.attr('role', 'radiogroup')
    group.attr('aria-label', title)
    base.append(group)

    for (const choice of choices) {
        const row = Button()
        row.addClass(style.choice)
        row.attr('role', 'radio')

        const label = Div(choice.label)
        label.addClass(style.choiceLabel)
        row.append(label)

        if (choice.note) {
            const note = Div(choice.note)
            note.addClass(style.choiceNote)
            row.append(note)
        }

        const sync = (on: boolean) => {
            row.toggleClass(style.choiceOn, on)
            row.attr('aria-checked', String(on))
        }
        sync(choice.value === selected)

        row.on('click', () => {
            for (const other of group.el.children) {
                other.classList.remove(style.choiceOn)
                other.setAttribute('aria-checked', 'false')
            }
            sync(true)
            base.emit('pick', choice.value)
        })

        group.append(row)
    }

    return base
}

// Which day this is for. Only ever in a flow the caller opened without naming
// one — from the day page the day is where you already are, and from the month
// it has to be asked. Emits 'pick' with the chosen day, and reads the choice
// back underneath in words, because three number fields do not tell you what
// weekday you just landed on.
export const DateSlide = (title: string, day: IDayCell) => {
    const base = Div()
    base.addClass(style.body)

    const heading = Div(title)
    heading.addClass(style.stepTitle)
    base.append(heading)

    // The calendar the user reads, so the fields are the ones they think in.
    // Hijri is the converter's business — a day is chosen here, not converted.
    const system = settings.get().system
    const startsAt: IDateParts = system === 'gregorian'
        ? { year: day.gy, month: day.gm, day: day.gd }
        : { year: day.jy, month: day.jm, day: day.jd }

    const read = Div()
    read.addClass(style.dayRead)

    const show = (cell: IDayCell) => read.html(paleYear(dayLine(
        calendar.dayAt(cell.gy, cell.gm, cell.gd, 0), system)))

    const picker = DatePicker(system, startsAt)
    picker.on('pick', (parts: IDateParts) => {
        const { gy, gm, gd } = convert(system, parts).g
        const cell = calendar.dayAt(gy, gm, gd, 0)
        show(cell)
        base.emit('pick', cell)
    })
    show(day)

    base.append(picker, read)

    return base
}
